import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import AnimeCard from "@/components/feature/AnimeCard";
import NewsCard from "@/components/feature/NewsCard";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { animeList } from "@/mocks/anime";
import { newsList } from "@/mocks/news";
import { filterAnime, searchAnime, searchNews } from "@/utils/content";

const hotKeywords = ["星海旅人", "悬疑", "治愈", "新番", "机械纪元", "剧情解析"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const tab = searchParams.get("tab") === "news" ? "news" : "anime";
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const animeResults = searchAnime(keyword);
  const newsResults = searchNews(keyword);
  const hotAnime = filterAnime(animeList, { sort: "popular" }).slice(0, 3);
  const hasKeyword = keyword.trim().length > 0;
  const total = animeResults.length + newsResults.length;

  useDocumentMeta(
    hasKeyword ? `动漫天堂 - “${keyword}”的搜索结果` : "动漫天堂 - 搜索结果",
    hasKeyword
      ? `在动漫天堂搜索“${keyword}”，共找到 ${total} 条相关动漫与资讯结果，支持动漫与资讯分类切换查看。`
      : "输入关键词搜索动漫作品与动漫资讯，支持作品名称、类型、地区与资讯内容全文检索。",
    hasKeyword
      ? `${keyword},动漫搜索,${keyword}动漫,动漫资讯搜索,动漫天堂`
      : "动漫搜索,动漫资讯搜索,动漫天堂,动漫资源",
    "/search"
  );

  const submit = (value: string) => {
    const next = value.trim();
    const params = new URLSearchParams();
    if (next) params.set("q", next);
    if (tab === "news") params.set("tab", "news");
    setSearchParams(params);
  };

  const switchTab = (nextTab: "anime" | "news") => {
    const params = new URLSearchParams();
    if (keyword) params.set("q", keyword);
    if (nextTab === "news") params.set("tab", "news");
    setSearchParams(params);
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteNavbar />

      <main className="flex-1 pt-16 md:pt-20">
        <section className="w-full border-b border-background-200/80 bg-background-100/60">
          <div className="mx-auto w-full max-w-page px-4 pb-9 pt-6 md:px-6 md:pb-12 md:pt-10">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <span className="text-foreground-800">搜索</span>
            </nav>

            <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
              搜索动漫与资讯
            </h1>
            <p className="mt-2 text-sm leading-7 text-foreground-600">
              支持作品名称、类型、地区、剧情简介与资讯正文的全文关键词检索。
            </p>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                submit(input);
              }}
              className="mt-6 flex w-full max-w-2xl items-center gap-2 rounded-lg border border-background-300 bg-background-50 p-2"
            >
              <span className="pl-2 text-lg text-foreground-400">
                <i className="ri-search-line" />
              </span>
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入动漫名称、类型或资讯关键词"
                className="min-w-0 flex-1 bg-transparent px-1 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>

            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-foreground-500">热门搜索：</span>
              {hotKeywords.map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => submit(word)}
                  className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  {word}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="mx-auto w-full max-w-page px-4 py-8 md:px-6 md:py-10">
          {hasKeyword ? (
            <>
              <div className="flex flex-col gap-3 border-b border-background-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-foreground-600">
                  关键词 <span className="font-semibold text-foreground-950">“{keyword}”</span> 共找到{" "}
                  <span className="font-heading font-bold text-primary-600">{total}</span> 条结果
                </p>
                <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
                  <button
                    type="button"
                    onClick={() => switchTab("anime")}
                    className={[
                      "whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors",
                      tab === "anime" ? "bg-background-50 text-primary-600" : "text-foreground-600",
                    ].join(" ")}
                  >
                    动漫 {animeResults.length}
                  </button>
                  <button
                    type="button"
                    onClick={() => switchTab("news")}
                    className={[
                      "whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors",
                      tab === "news" ? "bg-background-50 text-primary-600" : "text-foreground-600",
                    ].join(" ")}
                  >
                    资讯 {newsResults.length}
                  </button>
                </div>
              </div>

              {tab === "anime" ? (
                animeResults.length > 0 ? (
                  <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                    {animeResults.map((anime) => (
                      <AnimeCard key={anime.id} anime={anime} />
                    ))}
                  </div>
                ) : (
                  <EmptyState keyword={keyword} type="动漫" onTab={() => switchTab("news")} otherCount={newsResults.length} />
                )
              ) : newsResults.length > 0 ? (
                <div className="mt-6 flex flex-col gap-3 md:gap-4">
                  {newsResults.map((news) => (
                    <NewsCard key={news.id} news={news} variant="row" />
                  ))}
                </div>
              ) : (
                <EmptyState keyword={keyword} type="资讯" onTab={() => switchTab("anime")} otherCount={animeResults.length} />
              )}
            </>
          ) : (
            <div>
              <div className="rounded-lg border border-background-200 bg-background-100/60 px-6 py-14 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-2xl text-primary-600">
                  <i className="ri-search-eye-line" />
                </span>
                <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">输入关键词开始搜索</h2>
                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-foreground-600">
                  可以试试作品名称、类型标签，或者资讯里的关键词，比如「悬疑」「新番」「制作访谈」。
                </p>
              </div>

              <div className="mt-10">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">大家都在看</h2>
                  <Link to="/category" className="text-xs font-semibold text-primary-600 hover:text-primary-700">
                    浏览全部动漫
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5">
                  {hotAnime.map((anime) => (
                    <AnimeCard key={anime.id} anime={anime} />
                  ))}
                </div>
              </div>

              <div className="mt-10">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">最新资讯</h2>
                  <Link to="/news" className="text-xs font-semibold text-primary-600 hover:text-primary-700">
                    全部资讯
                  </Link>
                </div>
                <div className="flex flex-col gap-3">
                  {newsList.slice(0, 2).map((news) => (
                    <NewsCard key={news.id} news={news} variant="row" />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

interface EmptyStateProps {
  keyword: string;
  type: string;
  otherCount: number;
  onTab: () => void;
}

function EmptyState({ keyword, type, otherCount, onTab }: EmptyStateProps) {
  return (
    <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
        <i className="ri-emotion-sad-line" />
      </span>
      <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">
        没有找到与“{keyword}”相关的{type}
      </h2>
      <p className="mt-2 max-w-sm text-sm leading-6 text-foreground-600">
        试试更换关键词，或减少关键词长度。也可以切换到另一类结果继续浏览。
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        {otherCount > 0 && (
          <button
            type="button"
            onClick={onTab}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-arrow-left-right-line" />
            查看另一类结果（{otherCount}）
          </button>
        )}
        <Link
          to="/category"
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 hover:border-primary-400 hover:text-primary-600"
        >
          返回动漫分类
        </Link>
      </div>
    </div>
  );
}